/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';
import { ToggleDetail } from '#UNSAFE_Collection';
import { useUser } from '#hooks/UNSAFE_useUser';

/**
 * A hook that handles mouse and keyboard gesture that toggles the expanded state.
 * @param keyExtractor
 * @param expanded
 * @param onToggle
 * @returns
 */
export function useExpandCollapse<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  isExpanded: (key: K) => boolean,
  onToggle?: (detail: ToggleDetail<K>) => void,
  allowArrowKeysOnly?: boolean
) {
  const { direction } = useUser();

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (allowArrowKeysOnly !== true && onToggle) {
        handleToggle(event, keyExtractor, onToggle);
      }
    },
    [keyExtractor, onToggle, allowArrowKeysOnly]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (allowArrowKeysOnly !== true && (event.key === 'Enter' || event.key === ' ')) {
        handleToggle(event, keyExtractor, onToggle);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const isRtl = direction === 'rtl';
        const isLeftArrow = event.key === 'ArrowLeft' && !isRtl;
        handleArrowKey(
          isLeftArrow,
          event.target as HTMLElement,
          isExpanded,
          keyExtractor,
          onToggle
        );
        // arrow key might cause it to scroll horizontally
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [keyExtractor, onToggle, isExpanded, direction, allowArrowKeysOnly]
  );

  // prevent default (propagation) for keyDown to prevent scrolling the container
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (allowArrowKeysOnly !== true && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
      }
    },
    [allowArrowKeysOnly]
  );

  return onToggle ? { onClick, onKeyDown, onKeyUp } : {};
}

const handleArrowKey = <K>(
  isLeftArrow: boolean,
  target: HTMLElement,
  isExpanded: (key: K) => boolean,
  keyExtractor: (element: HTMLElement) => K | null,
  onToggle?: (detail: ToggleDetail<K>) => void
) => {
  const itemKey = keyExtractor(target);
  if (onToggle && itemKey) {
    const contains = isExpanded(itemKey);
    if ((isLeftArrow && contains) || (!isLeftArrow && !contains)) {
      onToggle({ value: itemKey });
    }
  }
};

const handleToggle = <K>(
  event: Event,
  keyExtractor: (element: HTMLElement) => K | null,
  onToggle?: (detail: ToggleDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (onToggle && itemKey) {
    onToggle({ value: itemKey });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};
